import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ogImage } from '../../lib/ogImage';
import { Instagram, ChevronRight } from 'lucide-react';
import BlogLayout from '../../components/BlogLayout';

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    { "@type": "Question", name: "Ποια η διαφορά μεταξύ blackwork και fine line;", acceptedAnswer: { "@type": "Answer", text: "Το blackwork βασίζεται σε contrast, έντονη παρουσία και bold σύνθεση. Το fine line δουλεύει με λεπτές γραμμές, διακριτικότητα και λεπτομέρεια. Διαφορετική αισθητική, διαφορετικές τεχνικές απαιτήσεις." } },
    { "@type": "Question", name: "Πότε αξίζει να επιλέξω realism;", acceptedAnswer: { "@type": "Answer", text: "Αν θέλεις portrait, dramatic imagery ή υψηλή φωτορεαλιστική λεπτομέρεια. Απαιτεί artist με πραγματική εξειδίκευση, δεν ταιριάζει σε κάθε studio." } },
    { "@type": "Question", name: "Πώς να βρω ποιο στυλ μου ταιριάζει;", acceptedAnswer: { "@type": "Answer", text: "Δες όχι μόνο τι σου αρέσει online, αλλά τι θα θέλεις να φοράς πάνω σου χρόνια. Και ρώτα τον artist, συχνά ένα consultation αποκαλύπτει καλύτερο direction από αυτό που είχες αρχικά στο μυαλό." } },
  ],
};

export default function TattooStyleGuideAthens() {
  useEffect(() => {
    const inject = (id: string, s: object) => {
      let tag = document.getElementById(id);
      if (!tag) { tag = document.createElement('script'); tag.id = id; (tag as HTMLScriptElement).type = 'application/ld+json'; document.head.appendChild(tag); }
      tag.textContent = JSON.stringify(s);
    };
    const articleSchema = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": "Blackwork, Fine Line ή Realism; Ποιο tattoo style σου ταιριάζει",
      "description": "Πώς να επιλέξεις το σωστό tattoo style: blackwork, fine line ή realism. Τι χαρακτηρίζει κάθε στυλ και πώς να βρεις αυτό που ταιριάζει στην αισθητική σου.",
      "image": "https://210tattoo.gr/og-image.png",
      "datePublished": "2026-04-24",
      "dateModified": "2026-04-24",
      "author": { "@type": "Organization", "name": "210 Tattoo Athens", "url": "https://210tattoo.gr" },
      "publisher": { "@id": "https://210tattoo.gr/#tattooparlor" },
      "url": "https://210tattoo.gr/blog/tattoo-style-guide-athens",
      "inLanguage": "el"
    };
    inject('schema-style-guide-faq', faqSchema);
    inject('schema-style-guide-article', articleSchema);
    return () => {
      document.getElementById('schema-style-guide-faq')?.remove();
      document.getElementById('schema-style-guide-article')?.remove();
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Blackwork, Fine Line ή Realism; Ποιο Στυλ Τατουάζ να Διαλέξεις | 210 Tattoo Athens</title>
        <meta name="description" content="Πώς να επιλέξεις το σωστό tattoo style: τι χαρακτηρίζει blackwork, fine line και realism, και πώς να βρεις αυτό που ταιριάζει στην αισθητική σου." />
        <link rel="canonical" href="https://210tattoo.gr/blog/tattoo-style-guide-athens" />
        <link rel="alternate" hrefLang="el" href="https://210tattoo.gr/blog/tattoo-style-guide-athens" />
        <link rel="alternate" hrefLang="x-default" href="https://210tattoo.gr/blog/tattoo-style-guide-athens" />
        <meta property="og:title" content="Blackwork, Fine Line ή Realism; Ποιο Στυλ Τατουάζ να Διαλέξεις | 210 Tattoo Athens" />
        <meta property="og:description" content="Τι χαρακτηρίζει blackwork, fine line και realism και πώς να βρεις το στυλ που ταιριάζει στην αισθητική σου." />
        <meta property="og:url" content="https://210tattoo.gr/blog/tattoo-style-guide-athens" />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={ogImage('tattoo-style-guide-athens')} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Ποιο Στυλ Τατουάζ να Διαλέξεις | 210 Tattoo Athens" />
        <meta name="twitter:description" content="Blackwork, fine line ή realism; Οδηγός για να βρεις το στυλ που ταιριάζει στην αισθητική σου." />
        <meta name="twitter:image" content={ogImage('tattoo-style-guide-athens')} />
      </Helmet>

      <BlogLayout>
        {/* Hero */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-pink-950/20 via-black to-black pointer-events-none" />
          <div className="max-w-4xl mx-auto relative z-10">
            <div className="flex items-center gap-2 text-pink-500 text-sm font-medium mb-6">
              <a href="/" className="hover:text-pink-400 transition-colors">Home</a>
              <ChevronRight size={14} />
              <span>Στυλ Τατουάζ</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              <span className="bg-gradient-to-r from-pink-500 via-pink-400 to-pink-300 bg-clip-text text-transparent">Ποιο στυλ τατουάζ</span>
              <br />
              <span className="text-white">να διαλέξεις στην Αθήνα</span>
            </h1>
            <p className="text-gray-500 text-xs mb-4 tracking-widest uppercase">24 Απριλίου 2026</p>
            <p className="text-xl text-gray-300 mb-6 leading-relaxed max-w-2xl">
              Ένα από τα μεγαλύτερα λάθη είναι να ξεκινά κάποιος από το σχέδιο χωρίς να έχει αποφασίσει style. Το ίδιο concept μπορεί να αποδοθεί εντελώς διαφορετικά ανάλογα με το στυλ, και αυτό αλλάζει το τελικό αποτέλεσμα.
            </p>
            <p className="text-gray-400 mb-10 leading-relaxed max-w-2xl">
              Αν θες πρώτα μια πλήρη εικόνα των στυλ που δουλεύουμε, <Link to="/blog/tattoo-styles-athens" className="text-pink-400 hover:text-pink-300 underline">δες όλα τα στυλ τατουάζ</Link>.
            </p>
            <a href="https://ig.me/m/210tattoo" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 inline-flex items-center gap-3 shadow-2xl hover:shadow-pink-500/30 transform hover:scale-105">
              <Instagram size={20} />
              Βρες το Στυλ σου
            </a>
          </div>
        </section>

        {/* Blackwork */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Τι χαρακτηρίζει <span className="text-pink-500">το Blackwork</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Το blackwork βασίζεται σε contrast, έντονη παρουσία και σύνθεση. Μπορεί να κινηθεί από καθαρά γραφικά μοτίβα μέχρι πολύ σύνθετα έργα. Είναι ιδανικό για όσους θέλουν bold αισθητική. Η δυνατότητά του να καλύπτει μεγάλες επιφάνειες με συνέπεια και να γερνά καλά με τον χρόνο το καθιστά δημοφιλή επιλογή για σοβαρά, μακροπρόθεσμα projects.
            </p>
            <div className="space-y-4 mb-8">
              {[
                { title: 'Contrast και παρουσία', desc: 'Το blackwork κάνει statement. Δεν περνά απαρατήρητο, και αυτό είναι συνειδητή επιλογή.' },
                { title: 'Γραφικά μοτίβα', desc: 'Από geometric patterns μέχρι illustrative compositions. Το blackwork δίνει εξαιρετική ελευθερία σε abstract και graphic design.' },
                { title: 'Longevity', desc: 'Το μαύρο μελάνι κρατά πολύ καλά με τον χρόνο. Blackwork pieces γερνάνε με αξιοπρέπεια σε σχέση με πολύχρωμα ή εξαιρετικά λεπτά fine line.' },
                { title: 'Για ποιους ταιριάζει', desc: 'Όσοι θέλουν bold, visible statement με μακροπρόθεσμη αντοχή. Αν η αισθητική σου τείνει σε graphic design, illustration ή έντονα patterns, blackwork είναι φυσικό fit.' },
              ].map((item, i) => (
                <div key={i} className="flex gap-4 items-start">
                  <span className="w-2 h-2 rounded-full bg-pink-500 shrink-0 mt-2" />
                  <div>
                    <p className="text-white font-semibold mb-1">{item.title}</p>
                    <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Fine Line */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Πότε αξίζει να επιλέξεις <span className="text-pink-500">Fine Line</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Fine line λειτουργεί εξαιρετικά όταν θες λεπτομέρεια, διακριτικότητα και καθαρό design. Απαιτεί artist με εξειδίκευση και σταθερό χέρι, γιατί κάθε γραμμή είναι ορατή. Όταν ταιριάζει με το concept, δίνει αποτέλεσμα εκλεπτυσμένο και διαχρονικό.
            </p>
            <div className="grid sm:grid-cols-2 gap-4 mb-8">
              {[
                { title: 'Λεπτομέρεια και ακρίβεια', desc: 'Το fine line επιτρέπει εξαιρετικά λεπτές γραμμές και λεπτομέρεια που άλλα styles δεν μπορούν να αποδώσουν.' },
                { title: 'Διακριτικότητα', desc: 'Ιδανικό για όσους θέλουν κάτι που να είναι εκεί χωρίς να φωνάζει. Η παρουσία του fine line είναι subtle αλλά εκλεπτυσμένη.' },
                { title: 'Botanical, floral, delicate concepts', desc: 'Φυτά, λεπτά σύμβολα, delicate compositions αποδίδονται καλύτερα σε fine line από οποιοδήποτε άλλο στυλ.' },
                { title: 'Όταν δεν ταιριάζει', desc: 'Δεν λειτουργεί σε placements με γρήγορο fading ή σε concepts που απαιτούν bold παρουσία. Η επιλογή style πρέπει να ταιριάζει με το concept.' },
              ].map((item, i) => (
                <div key={i} className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200">
                  <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Realism */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Για ποιους ταιριάζει <span className="text-pink-500">το Realism</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-6">
              Αν μιλάμε για portraits, dramatic imagery ή υψηλή λεπτομέρεια, realism είναι άλλη κατηγορία. Αλλά απαιτεί artist με πραγματική εξειδίκευση.
            </p>
            <div className="space-y-4 mb-8">
              {[
                { title: 'Portraits', desc: 'Η πιο απαιτητική εφαρμογή realism. Απαιτεί εξαιρετική τεχνική για να αποδοθούν χαρακτηριστικά που μοιάζουν φωτογραφικά.' },
                { title: 'Black & Gray Realism', desc: 'Χρησιμοποιεί διαβαθμίσεις γκρι για βάθος και texture. Γερνά πολύ καλά και είναι ένα από τα πιο technically demanding styles.' },
                { title: 'Dramatic imagery', desc: 'Ζώα, σκηνές, σύνθεσεις με βάθος. Αν θέλεις κάτι που να φαίνεται σαν φωτογραφία ή ζωγραφιά, realism είναι ο δρόμος.' },
                { title: 'Απαιτήσεις artist', desc: 'Δεν κάνει κάθε artist realism καλά. Χρειάζεσαι portfolio με αποδεδειγμένα healed realism projects, ιδιαίτερα portraits.' },
              ].map((item, i) => (
                <div key={i} className="flex gap-4 items-start">
                  <span className="w-2 h-2 rounded-full bg-pink-500 shrink-0 mt-2" />
                  <div>
                    <p className="text-white font-semibold mb-1">{item.title}</p>
                    <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How to find your style */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Πώς να βρεις στυλ που ταιριάζει <span className="text-pink-500">στην αισθητική σου</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Σκέψου τι θα θες να φοράς πάνω σου σε πέντε και δέκα χρόνια. Αυτό είναι πολύ πιο αξιόπιστο κριτήριο από αυτό που βλέπεις τώρα στο Instagram. Η αισθητική προτίμηση εξελίσσεται, και ένα τατουάζ που επιλέγεται με γνώμονα τη διαχρονικότητα σπάνια γίνεται αντικείμενο μεταμέλειας.
            </p>
            <div className="space-y-6">
              {[
                { step: '01', title: 'Μάζεψε references από διαφορετικά styles', desc: 'Βάλε στο ίδιο folder fine line, blackwork και realism που σου αρέσουν. Δες ποιο κατηγορία έχει περισσότερα, εκεί είναι η αισθητική σου.' },
                { step: '02', title: 'Σκέψου το concept πρώτα', desc: 'Αν ξέρεις τι θέλεις να αποδώσεις, κάποια styles είναι πιο κατάλληλα. Ένα botanical concept, ένα portrait και ένα geometric pattern οδηγούν σε διαφορετικά styles.' },
                { step: '03', title: 'Ρώτα στο consultation', desc: 'Πολλές φορές ένας καλός artist μπορεί να δει το concept σου και να προτείνει style direction που δεν είχες σκεφτεί, και να είναι καλύτερο.' },
              ].map((item) => (
                <div key={item.step} className="flex gap-6 items-start">
                  <div className="text-4xl font-bold text-pink-600/30 shrink-0 w-12 text-center leading-none pt-1">{item.step}</div>
                  <div className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 flex-1">
                    <h3 className="text-white font-semibold text-lg mb-2">{item.title}</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 210 style consultation */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Συμβουλευτική επιλογής στυλ <span className="text-pink-500">στο 210 Tattoo Athens</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-6">
              Συχνά βοηθάμε πελάτες να μετατρέψουν μια γενική ιδέα σε style direction. Και πολλές φορές εκεί γεννιέται καλύτερο concept από το αρχικό. Ένας συνηθισμένος κίνδυνος είναι να επιλέξεις style με βάση αυτό που είδες online, χωρίς να ελέγξεις αν το συγκεκριμένο concept σου ταιριάζει. Η συζήτηση στο consultation βοηθά να βρεθεί το σωστό ταίριασμα μεταξύ ιδέας και εκτέλεσης.
            </p>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Στο studio εξειδικευόμαστε κυρίως σε fine line και realism. Αυτό σημαίνει ότι έχουμε πραγματικό βάθος στα συγκεκριμένα styles με αποδεδειγμένα healed projects, και ότι θα σου πούμε ειλικρινά αν ένα concept ταιριάζει ή αν αξίζει να εξεταστεί διαφορετική κατεύθυνση.
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { title: 'Fine Line εξειδίκευση', desc: 'Portfolio με αποδεδειγμένα fine line projects, healed δουλειές και custom σχέδια.' },
                { title: 'Realism και Black & Gray', desc: 'Από portraits μέχρι dramatic compositions με βάθος και texture.' },
                { title: 'Ειλικρινής κατεύθυνση', desc: 'Αν το concept σου ταιριάζει καλύτερα σε άλλο style, θα σου το πούμε και θα σε παραπέμψουμε σε artist που εξειδικεύεται σε αυτό.' },
                { title: 'Custom από ιδέα σε σχέδιο', desc: 'Από μια λέξη ή εικόνα μέχρι το τελικό custom σχέδιο, καθοδηγούμε όλη τη διαδικασία σχεδιασμού πριν τη συνεδρία.' },
              ].map((item, i) => (
                <div key={i} className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200">
                  <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Θέλεις βοήθεια να βρεις <span className="text-pink-500">ποιο style σου ταιριάζει;</span></h2>
            <p className="text-gray-300 text-lg mb-8">
              Στείλε μας DM στο Instagram του 210 Tattoo Athens με την ιδέα σου και θα σε κατευθύνουμε. Δεν χρειάζεται να ξέρεις ακριβώς τι θέλεις, αρκεί να ξεκινήσεις τη συζήτηση.
            </p>
            <a href="https://ig.me/m/210tattoo" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 inline-flex items-center gap-3 shadow-2xl hover:shadow-pink-500/30 transform hover:scale-105">
              <Instagram size={20} />
              Στείλε DM για Style Consultation
            </a>
          </div>
        </section>

        {/* Related articles */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-white mb-8">Σχετικά <span className="text-pink-500">Άρθρα</span></h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Link to="/blog/fine-line-tattoo-guide-athens" className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200 group">
                <p className="text-pink-500 text-xs uppercase tracking-widest mb-2">Άρθρο</p>
                <h3 className="text-white font-semibold group-hover:text-pink-400 transition-colors">Fine Line Tattoos στην Αθήνα</h3>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed">Τι κάνει ένα fine line quality, πώς γερνά και τι να ρωτήσεις στο consultation.</p>
              </Link>
              <Link to="/blog/tattoo-artist-athens" className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200 group">
                <p className="text-pink-500 text-xs uppercase tracking-widest mb-2">Άρθρο</p>
                <h3 className="text-white font-semibold group-hover:text-pink-400 transition-colors">Πώς να διαλέξεις tattoo artist στην Αθήνα</h3>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed">Τι να προσέξεις πριν επιλέξεις studio: portfolio, hygiene και red flags.</p>
              </Link>
            </div>
          </div>
        </section>

        <section className="py-16 bg-gray-950 border-t border-pink-600/10">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-white mb-8 text-center">Υπηρεσίες <span className="text-pink-500">/ Services</span></h2>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="/fine-line-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Fine Line Tattoo Athens</a>
              <a href="/realistic-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Realistic Tattoo Athens</a>
              <a href="/cover-up-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Cover Up Tattoo Athens</a>
              <a href="/black-gray-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Black &amp; Gray Tattoo Athens</a>
              <a href="/custom-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Custom Tattoo Athens</a>
              <a href="/blackwork-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Blackwork Tattoo Athens</a>
              <a href="/dotwork-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Dotwork Tattoo Athens</a>
              <a href="/sleeve-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Sleeve Tattoo Αθήνα</a>
              <a href="/old-school-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Old School Tattoo Αθήνα</a>
              <a href="/lettering-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Lettering Tattoo Αθήνα</a>
            </div>
          </div>
        </section>
      </BlogLayout>
    </>
  );
}
